import { useCallback, useEffect } from "react";
import { useAuthStore } from "../store/authstore";
import { tokenManager, TOKEN_REFRESH_THRESHOLD } from "../services/api/axios";

// Proactively refresh access token when this many ms remain before expiry.
// Must stay comfortably above axios.ts's TOKEN_REFRESH_THRESHOLD (the request-time
// safety net) so this background poll is the one that normally does the refreshing.
const PROACTIVE_REFRESH_THRESHOLD = 5 * 60 * 1000;
if (PROACTIVE_REFRESH_THRESHOLD <= TOKEN_REFRESH_THRESHOLD) {
    throw new Error('PROACTIVE_REFRESH_THRESHOLD must be greater than axios TOKEN_REFRESH_THRESHOLD');
}

// Session lifetime is governed solely by the refresh token (7 days) — there is no
// separate idle-timeout logout. This hook only keeps the access token fresh and
// ends the session when the refresh token itself expires or is invalidated.
export function useSessionManager() {
    const { checkSession, updateLastActivity, logout } = useAuthStore();

    // Runs every 60 s: validates refresh-token expiry AND proactively refreshes
    // the access token if it is within PROACTIVE_REFRESH_THRESHOLD of expiry.
    // Uses silent=true so no toasts disturb the user during normal activity.
    const checkAndRefreshSession = useCallback(async () => {
        checkSession();

        const session = useAuthStore.getState().session;
        // checkSession() may have just logged us out (expired refresh token) — bail out
        // instead of proceeding with a null/stale session.
        if (!session?.expiresAt) return;

        const timeUntilExpiry = session.expiresAt - Date.now();
        if (timeUntilExpiry > 0 && timeUntilExpiry < PROACTIVE_REFRESH_THRESHOLD) {
            try {
                await tokenManager.refreshToken(
                    session,
                    useAuthStore.getState().updateSession,
                    useAuthStore.getState().logout,
                    true  // silent — no toasts for background refresh
                );
            } catch {
                // Ignore silently — the request interceptor handles it on the next API call
            }
        }
    }, [checkSession]);

    useEffect(() => {
        // Run immediately on mount too (not just on the first 60s tick) — covers
        // reopening a tab that was backgrounded past the access token's expiry,
        // refreshing before any protected API call fires instead of after.
        checkAndRefreshSession();
        updateLastActivity();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                updateLastActivity();
                checkAndRefreshSession();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        let broadcastChannel: BroadcastChannel | null = null;
        try {
            broadcastChannel = new BroadcastChannel('auth');
            broadcastChannel.onmessage = (event) => {
                if (event.data === 'logout') logout();
            };
        } catch {
            // BroadcastChannel not supported in all environments
        }

        // Session check + proactive token refresh every 60 s.
        const sessionCheckInterval = setInterval(checkAndRefreshSession, 60000);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            broadcastChannel?.close();
            clearInterval(sessionCheckInterval);
        };
    }, [updateLastActivity, logout, checkAndRefreshSession]);
}
